import { ArrowUpRight, Boxes, Check, Copy, FlipHorizontal2, Flag, House, LayoutGrid, LayoutTemplate, Link2, LocateFixed, type LucideIcon, Minus, Pentagon, Plus, RotateCw, Spline, StickyNote, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { counts, DEFAULT_STOREY, describe, formatStacks, materialName, normalizeSourceUrl, schematicDims, sourceHost, STATUS_LABEL, type PlanKind, type PlanObject, type PlanStatus } from "@mcp/plan";
import { SCHEMATIC_EXTENSIONS, SCHEMATIC_FORMAT_LABEL } from "@mcp/world";
import { MaterialPicker } from "./MaterialPicker.tsx";
import { controller } from "../app/controller.ts";
import { select, useApp } from "../state/store.ts";
import { ColorPicker } from "./ColorPicker.tsx";

const KIND: Record<PlanKind, { label: string; icon: LucideIcon; tone: string }> = {
  path: { label: "Path", icon: Spline, tone: "amber" },
  area: { label: "Area", icon: Pentagon, tone: "teal" },
  building: { label: "Building", icon: House, tone: "violet" },
  marker: { label: "Marker", icon: Flag, tone: "rose" },
};

function ago(t: number) {
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(t).toLocaleDateString();
}

/** Status as four segments (not a dropdown): the word always next to its colour. */
function StatusSeg({ value, onChange, mixed = false }: { value: PlanStatus | null; onChange: (s: PlanStatus) => void; mixed?: boolean }) {
  return (
    <div className="seg status-seg" role="radiogroup" aria-label={mixed ? "Status (mixed)" : "Status"}>
      {(Object.keys(STATUS_LABEL) as PlanStatus[]).map((k) => (
        <button key={k} role="radio" aria-checked={value === k} onClick={() => onChange(k)}>
          {value === k && <i className={`status-dot s-${k}`} />}{STATUS_LABEL[k]}
        </button>
      ))}
    </div>
  );
}

/** A number with − / + on either side. */
function Stepper({ value, min, max, onChange, label }: { value: number; min: number; max: number; onChange: (v: number) => void; label: string }) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <span className="stepper">
      <button className="btn btn-ghost" onClick={() => onChange(clamp(value - 1))} disabled={value <= min} aria-label={`${label}: less`}><Minus size={13} aria-hidden="true" /></button>
      <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(clamp(+e.target.value || min))} aria-label={label} />
      <button className="btn btn-ghost" onClick={() => onChange(clamp(value + 1))} disabled={value >= max} aria-label={`${label}: more`}><Plus size={13} aria-hidden="true" /></button>
    </span>
  );
}

export function Inspector() {
  const sel = useApp((s) => s.plans.find((p) => p.id === s.selectedId));
  const count = useApp((s) => s.selectedIds.length);
  const readOnly = useApp((s) => s.readOnly);
  useApp((s) => s.boardTick); // re-render when cards change (board count)
  // nothing selected: no panel (more room for the map) — hints are on the map, shortcuts under "?"
  if (!sel) return null;
  if (count > 1) return <MultiInspector />;
  const up = (patch: Partial<PlanObject>) => controller.update(sel.id, patch);
  const boardCount = controller.store?.listCards(sel.id).length ?? 0;
  const groupSize = sel.group ? useApp.getState().plans.filter((p) => p.group === sel.group).length : 0;
  const k = KIND[sel.kind];
  return (
    <aside className="panel inspector" data-tour="inspector" aria-label={`Inspector: ${sel.name}`}>
      <div className="panel-head">
        <span className={`chip kind-chip tone-${k.tone}`}><k.icon size={12} aria-hidden="true" />{k.label}{sel.schematic ? " · schematic" : ""}</span>
        <span className="head-actions">
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => controller.focus(sel)} title="Zoom to it" aria-label="Zoom to it"><LocateFixed size={15} aria-hidden="true" /></button>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => select([])} title="Close · Esc" aria-label="Close"><X size={15} aria-hidden="true" /></button>
        </span>
      </div>
      <div className="insp-scroll">
        <div className="insp-top">
          <label className="insp-name"><span className="sr-only">Name</span><input value={sel.name} onChange={(e) => up({ name: e.target.value })} spellCheck={false} /></label>
          <div className="insp-meta tabular">{describe(sel)}{sel.author ? ` · ${sel.author}, ${ago(sel.updatedAt)}` : ""}</div>
          {sel.group && (
            <div className="group-line">
              <span>Part of <b>{sel.groupName ?? "a group"}</b></span>
              <button className="link" onClick={() => controller.selectGroup(sel.group!)}>Select all {groupSize}</button>
            </div>
          )}
          <StatusSeg value={sel.status} onChange={(v) => up({ status: v })} />
        </div>

        <div className="insp-body">
          {(sel.kind === "area" || sel.kind === "path") && !readOnly && (
            <button className="btn btn-outline btn-block layout-btn" onClick={() => controller.startFill(sel.id)} title="Generate streets, plots and houses (key 6)">
              {sel.kind === "area" ? <><LayoutGrid size={15} aria-hidden="true" />Fill with a layout…</> : <><House size={15} aria-hidden="true" />Village along this path…</>}
            </button>
          )}
          {sel.schematic && <SchematicSection o={sel} />}
          {sel.kind !== "marker" && !sel.schematic && (
            <div className="field">
              <span className="overline">{sel.kind === "building" ? "Wall material" : "Material"}</span>
              <MaterialPicker value={sel.material} kind={sel.kind} onChange={(m) => up({ material: m })} />
            </div>
          )}
          {sel.kind === "path" && (
            <div className="field-row">
              <span>Width</span>
              <Stepper value={sel.width} min={1} max={32} onChange={(v) => up({ width: v })} label="Width in blocks" />
            </div>
          )}
          {sel.kind === "building" && sel.schematic && (
            <div className="field-row" title="The schematic's bottom layer sits on this block level (lower it by 1 if the file includes the ground)">
              <span>Base Y</span>
              <Stepper value={sel.y} min={-64} max={320} onChange={(v) => up({ y: v })} label="Base Y" />
            </div>
          )}
          {sel.kind === "building" && !sel.schematic && (
            <div className="grid3 nums">
              <label>Base Y<input type="number" value={sel.y} onChange={(e) => up({ y: +e.target.value })} /></label>
              <label>Height<input type="number" min={1} max={384} value={sel.height} onChange={(e) => up({ height: Math.max(1, +e.target.value) })} /></label>
              <label title="Blocks per storey (floor to floor)">Storey<input type="number" min={2} max={32} value={sel.storey ?? DEFAULT_STOREY} onChange={(e) => up({ storey: Math.max(2, +e.target.value) })} /></label>
            </div>
          )}
          {sel.kind === "marker" && (
            <div className="field-row">
              <span>Y</span>
              <Stepper value={sel.y} min={-64} max={320} onChange={(v) => up({ y: v })} label="Y" />
            </div>
          )}
          <div className="field">
            <span className="overline">Colour</span>
            <ColorPicker value={sel.color} kind={sel.kind} onChange={(c) => up({ color: c })} />
          </div>
          {!sel.schematic && <Counts o={sel} />}
          <label className="field">
            <span className="overline">Notes</span>
            <textarea rows={4} value={sel.notes} placeholder="Materials, style, who builds it…" onChange={(e) => up({ notes: e.target.value })} />
          </label>
          <SourceLink o={sel} />
          <details className="insp-details">
            <summary>Coordinates <span className="tabular">({sel.points.length})</span></summary>
            <ol className="coords">{sel.points.map(([x, z], i) => <li key={i}><code>{x} {z}</code></li>)}</ol>
            <p className="caption">{sel.schematic ? "A schematic keeps its size: drag it to move it, R turns it, F mirrors it." : <>Drag the white corner handles to reshape{sel.kind === "building" ? " (rectangles stay rectangular — hold Shift to move one corner freely)" : ""}. Drag the object itself to move it.</>}</p>
          </details>
          <p className="insp-tip">Move: drag it · <kbd>Alt</kbd> + arrows (<kbd>Shift</kbd> = 10) · Rename: double-click its name tag or <kbd>F2</kbd> · Copy, paste: <kbd>Ctrl C</kbd> <kbd>Ctrl V</kbd> at the mouse</p>
        </div>
      </div>
      <div className="insp-foot">
        <button className={`btn board-btn${boardCount ? " has" : ""}`} onClick={() => controller.openBoard(sel.id)} title="Pinboard: notes, checklists, images and links for this object">
          <StickyNote size={15} aria-hidden="true" />Pinboard{boardCount ? <span className="count tabular">{boardCount}</span> : null}
        </button>
        <span className="spacer" />
        <button className="btn btn-ghost btn-icon" onClick={() => controller.tools?.duplicateSelection()} title="Duplicate · Ctrl D" aria-label="Duplicate"><Copy size={15} aria-hidden="true" /></button>
        {sel.points.length > 1 && <button className="btn btn-ghost btn-icon" onClick={() => controller.tools?.rotateSelection()} title="Rotate 90° · R" aria-label="Rotate 90°"><RotateCw size={15} aria-hidden="true" /></button>}
        <button className="btn btn-ghost btn-icon btn-danger" onClick={() => controller.remove(sel.id)} title="Delete · Del" aria-label={`Delete ${sel.name}`}><Trash2 size={15} aria-hidden="true" /></button>
      </div>
    </aside>
  );
}

function MultiInspector() {
  const ids = useApp((s) => s.selectedIds);
  const plans = useApp((s) => s.plans);
  const sel = plans.filter((p) => ids.includes(p.id));
  const first = sel[0];
  const all = (patch: Partial<PlanObject>) => controller.store?.updateMany(sel.map((o) => ({ id: o.id, patch })));
  const sameStatus = sel.every((o) => o.status === first?.status);
  return (
    <aside className="panel inspector multi" aria-label={`${sel.length} objects selected`}>
      <div className="panel-head">
        <b className="insp-title"><span className="chip tone-neutral tabular">{sel.length}</span>{sel.length} objects selected</b>
        <span className="head-actions">
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => select([])} title="Close · Esc" aria-label="Close"><X size={15} aria-hidden="true" /></button>
        </span>
      </div>
      <div className="insp-scroll">
        <div className="insp-top">
          <ul className="multi-list">
            {sel.map((o) => (
              <li key={o.id}>
                <i className="swatch" style={{ background: o.color }} /><span className="grow">{o.name}</span>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => select(ids.filter((x) => x !== o.id))} title={`Take ${o.name} out of the selection`} aria-label={`Take ${o.name} out of the selection`}><X size={13} aria-hidden="true" /></button>
              </li>
            ))}
          </ul>
          <p className="measure">Together <b className="tabular">{sel.reduce((a, o) => a + (o.kind === "marker" ? 0 : counts(o).blocks), 0).toLocaleString("en-US")}</b> blocks covered</p>
          <StatusSeg value={sameStatus ? first?.status ?? null : null} mixed={!sameStatus} onChange={(v) => all({ status: v })} />
        </div>
        <div className="insp-body">
          <div className="field">
            <span className="overline">Colour (all)</span>
            <ColorPicker value={first?.color ?? "#ffffff"} onChange={(c) => all({ color: c })} />
          </div>
          {first && first.kind !== "marker" && sel.every((o) => o.kind === first.kind) && (
            <div className="field">
              <span className="overline">Material (all)</span>
              <MaterialPicker value={sel.every((o) => o.material === first.material) ? first.material : undefined} kind={first.kind} onChange={(m) => all({ material: m })} />
            </div>
          )}
          <button className="btn btn-outline btn-block layout-btn" onClick={() => controller.openLayouts("templates")} title="Reuse these objects anywhere"><LayoutTemplate size={15} aria-hidden="true" />Save as template…</button>
          <p className="insp-tip">Drag any selected object to move them together · <kbd>Shift</kbd> + click adds or removes · <kbd>Ctrl A</kbd> selects all</p>
        </div>
      </div>
      <div className="insp-foot">
        <button className="btn btn-ghost" onClick={() => controller.tools?.duplicateSelection()} title="Duplicate · Ctrl D"><Copy size={15} aria-hidden="true" />Duplicate</button>
        <button className="btn btn-ghost" onClick={() => controller.tools?.rotateSelection()} title="Rotate 90° · R"><RotateCw size={15} aria-hidden="true" />Rotate</button>
        <span className="spacer" />
        <button className="btn btn-ghost btn-danger" onClick={() => controller.tools?.deleteSelection()} title="Delete all · Del"><Trash2 size={15} aria-hidden="true" />Delete all</button>
      </div>
    </aside>
  );
}

/** The numbers that matter when building it. */
function Counts({ o }: { o: PlanObject }) {
  if (o.kind === "marker") return <div className="measure mono">x {o.points[0]?.[0]} · y {o.y} · z {o.points[0]?.[1]}</div>;
  const c = counts(o);
  const n = (v: number | undefined) => (v ?? 0).toLocaleString("en-US");
  const rows: [string, string, string?][] = [];
  const pre = c.approx ? "≈ " : "";
  const stk = (v: number | undefined) => ((v ?? 0) >= 64 ? formatStacks(v ?? 0) : "");
  const mat = o.material ? materialName(o.material) : "";
  /** "Gravel · 13 stacks + 38" */
  const st = (v: number | undefined) => [mat, stk(v)].filter(Boolean).join(" · ");
  if (o.kind === "area") {
    rows.push(["Blocks covered", pre + n(c.blocks), st(c.blocks)]);
    rows.push(["Size", `${c.w} × ${c.d}`]);
  } else if (o.kind === "path") {
    rows.push(["Blocks to pave", pre + n(c.blocks), st(c.blocks)]);
    rows.push(["Length", `${n(c.length)} blocks`]);
    rows.push(["Width", `${o.width}`]);
  } else {
    rows.push(["Footprint", pre + n(c.blocks), `${c.w} × ${c.d}`]);
    rows.push(["Walls", n(c.walls), `${n(c.ring)} around × ${o.height} high${st(c.walls) ? " = " + st(c.walls) : ""}`]);
    rows.push(["Floor / storey", n(c.floor), `${c.storeys} storey${c.storeys === 1 ? "" : "s"} of ${c.storeyHeight}`]);
    rows.push(["Roof", n(c.roof), st(c.roof)]);
    rows.push(["Shell total", n(c.shell), `${st(c.shell) ? st(c.shell) + " · " : ""}walls + upper floors + roof`]);
    rows.push(["Volume", n(c.volume)]);
  }
  return (
    <table className="counts">
      <tbody>
        {rows.map(([k, v, hint]) => (
          <tr key={k}><th>{k}</th><td><b>{v}</b>{hint && <span className="hint">{hint}</span>}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

const ACCEPT = SCHEMATIC_EXTENSIONS.join(",");

/** Where it's from: the original post of a downloaded schematic, a video, a Pinterest idea … (any object). */
function SourceLink({ o }: { o: PlanObject }) {
  const [text, setText] = useState(o.sourceUrl ?? "");
  useEffect(() => setText(o.sourceUrl ?? ""), [o.id, o.sourceUrl]);
  const valid = normalizeSourceUrl(text);
  const commit = () => {
    if (!text.trim()) { if (o.sourceUrl) controller.update(o.id, { sourceUrl: undefined }); return; }
    if (valid && valid !== o.sourceUrl) controller.update(o.id, { sourceUrl: valid });
  };
  return (
    <div className="field source-link">
      <span className="overline">Source link</span>
      <div className={`field-box sm${text.trim() && !valid ? " invalid" : ""}`}>
        <Link2 size={14} aria-hidden="true" />
        <input type="url" value={text} placeholder="https://…" onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }} aria-label="Source link" />
        {o.sourceUrl && <a className="btn btn-sm btn-ghost" href={o.sourceUrl} target="_blank" rel="noopener noreferrer" title={o.sourceUrl}>Open<ArrowUpRight size={13} aria-hidden="true" /></a>}
      </div>
      {text.trim() && !valid ? <span className="field-error">Only web links (https://…)</span> : <span className="field-hint">Where it's from: the original post, a video…</span>}
    </div>
  );
}

/** A building placed from a schematic file: file, size, the file on this computer?, turn/mirror, materials. */
function SchematicSection({ o }: { o: PlanObject }) {
  const ref = o.schematic!;
  const have = useApp((s) => s.schematics.some((e) => e.hash === ref.hash));
  const importing = useApp((s) => s.schemImporting);
  const [different, setDifferent] = useState<File | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { setDifferent(null); setShowAll(false); }, [o.id]);
  const [w, l] = schematicDims(ref);
  const mats = ref.materials ?? [];
  const shown = showAll ? mats : mats.slice(0, 10);
  const n = (v: number) => v.toLocaleString("en-US");
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setDifferent(null);
    if ((await controller.attachSchematicFile(o.id, f)) === "different") setDifferent(f);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(controller.materialsText(o)); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard blocked */ }
  };
  return (
    <div className="schem-box">
      <div className="schem-title">
        <span className="schem-icon" aria-hidden="true"><Boxes size={16} /></span>
        <span className="grow">
          <b title={ref.file}>{ref.file}</b>
          <span className="caption">{SCHEMATIC_FORMAT_LABEL[ref.format]}{ref.author ? ` · by ${ref.author}` : ""}</span>
        </span>
      </div>
      <div className="schem-dims tabular">{w} × {l} · {ref.size[1]} high · <b>{n(ref.blocks)}</b> blocks{ref.turns || ref.mirror ? <span className="muted"> · turned {ref.turns * 90}°{ref.mirror ? ", mirrored" : ""}</span> : null}</div>
      {!have && (
        <div className="schem-missing-box">
          <p>
            <b>The schematic file isn't on this computer</b>, so you see a plain box.{" "}
            {o.sourceUrl
              ? <>Download it from <a href={o.sourceUrl} target="_blank" rel="noopener noreferrer">{sourceHost(o.sourceUrl)}<ArrowUpRight className="trail" size="1em" aria-hidden="true" /></a>, then import it:</>
              : <>Ask whoever placed it where it's from, then import the file:</>}
          </p>
          <button className="btn btn-sm btn-primary" disabled={importing} onClick={() => input.current?.click()}>{importing ? "Reading…" : "Import the file…"}</button>
        </div>
      )}
      {different && (
        <div className="schem-missing-box">
          <p>“{different.name}” is a different file than the one this building was made from ({ref.file}).</p>
          <div className="row">
            <button className="btn btn-sm btn-primary" onClick={async () => { await controller.attachSchematicFile(o.id, different, true); setDifferent(null); }}>Use it for “{o.name}”</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setDifferent(null)}>Keep the old one</button>
          </div>
        </div>
      )}
      <input ref={input} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; void pick(f); }} />
      <div className="schem-actions">
        <button className="btn btn-sm btn-outline" onClick={() => controller.tools?.rotateSelection()} title="Turn 90° · R"><RotateCw size={14} aria-hidden="true" />Turn 90°</button>
        <button className="btn btn-sm btn-outline" onClick={() => controller.tools?.mirrorSchematics()} title="Mirror · F"><FlipHorizontal2 size={14} aria-hidden="true" />Mirror</button>
      </div>
      {mats.length > 0 && (
        <>
          <div className="row between mats-head"><span className="overline">Materials</span><button className="btn btn-sm btn-ghost" onClick={copy} title="Copy the list as text">{copied ? <><Check size={14} aria-hidden="true" />Copied</> : <><Copy size={14} aria-hidden="true" />Copy list</>}</button></div>
          <table className="counts mats">
            <tbody>
              {shown.map(([id, c]) => {
                const m = controller.material(controller.knownBlock(id));
                return (
                  <tr key={id}>
                    <th>{m ? <img className="mat-thumb" src={controller.materialThumb(m, "side")} alt="" /> : <i className="mat-thumb none" />}{controller.itemName(id)}</th>
                    <td><b>{n(c)}</b>{c >= 64 && <span className="hint">{formatStacks(c)}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {mats.length > 10 && <button className="link tiny" onClick={() => setShowAll(!showAll)}>{showAll ? "Show fewer" : `Show all ${mats.length} kinds`}</button>}
        </>
      )}
    </div>
  );
}
